import { useEffect, useCallback, useContext } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { useModules } from "../context/modules"
import { MODULE_TYPES } from "../modules/types"
import { ARTIFACT_KINDS } from "../modules/ai-artifact/types"
import { usePinWorkflow } from "connected-components/ai-teammate/hooks/use-pin-workflow"
import { log } from "helpers/logger"
import { getErrorMessage } from "helpers/dev-logger"
import { DashboardContext } from "../context/dashboard"
/**
 * Hook that listens for artifact pin requests from AI Teammate and handles them
 */
export function usePinArtifactHandler() {
  const { addModule, updateModule } = useModules()
  const { mutateAsync: pinWorkflow } = usePinWorkflow()
  const accountAlias = useAccountAlias()
  const queryClient = useQueryClient()
  const { modelId, instrument, base, startDate, endDate } = useContext(DashboardContext) ?? {}

  const handlePinRequest = useCallback(async ({ artifact, kind, title }) => {
    let moduleId

    try {
      // Add module with optimistic state
      moduleId = addModule({
        type: MODULE_TYPES.AI_ARTIFACT,
        config: {
          kind,
          title: title || `AI ${kind === ARTIFACT_KINDS.PLOT ? "Plot" : "Table"}`,
          isPinning: true,
        },
      })

      // Store artifact in TanStack Query cache for optimistic rendering
      queryClient.setQueryData(
        ["optimistic-artifact", moduleId],
        artifact,
      )

      // Call API to pin workflow - backend generates and returns pinnedId
      const result = await pinWorkflow({
        accountAlias,
        artifactId: artifact.id,
        title: title || `AI ${kind === ARTIFACT_KINDS.PLOT ? "Plot" : "Table"}`,
      })

      // Seed React Query cache with the artifact using the correct cache key
      // This prevents refetch when RestDataIntegrator mounts
      const cacheKey = [
        "ai-artifact",
        result.pinnedId,
        {
          modelId,
          positionSetId: instrument?.id,
          positionSetType: instrument?.type,
          baseId: base?.id,
          baseType: base?.type,
          startDate,
          endDate,
          accountAlias,
        },
      ]

      queryClient.setQueryData(cacheKey, { artifacts: [artifact] })

      // Store backend-generated pinnedId in config
      updateModule(moduleId, {
        config: {
          kind,
          title: title || `AI ${kind === ARTIFACT_KINDS.PLOT ? "Plot" : "Table"}`,
          pinnedId: result.pinnedId,
          isPinning: false,
        },
      })

      // Remove optimistic cache now that real cache is seeded
      queryClient.removeQueries({ queryKey: ["optimistic-artifact", moduleId] })

      // Emit success event (for analytics, notifications, etc.)
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId,
        pinnedId: result.pinnedId,
        kind,
      })
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      log(`Failed to pin artifact: ${errorMessage}`)

      if (moduleId) {
        // Keep cache for retry, just update error state
        updateModule(moduleId, {
          config: {
            isPinning: false,
            hasError: true,
          },
        })
      }

      // Emit failure event
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        moduleId,
        kind,
      })
    }
  }, [addModule, updateModule, pinWorkflow, accountAlias, queryClient, modelId, instrument, base, startDate, endDate])

  // Subscribe to pin requests
  useEffect(() => {
    const cleanupFn = dashboardEvents.on(
      DASHBOARD_EVENTS.ARTIFACT_PIN_REQUESTED,
      handlePinRequest,
    )

    return cleanupFn
  }, [handlePinRequest])
}
